import React, { useState, useContext } from 'react';
import AppContext from '../../contexts/AppContext';
import { Flag, Navigation, X } from 'lucide-react';
import * as firestoreService from '../../services/firestoreService';
import { isUserPremium } from '../../utils/premium';
import CaddieProGate from '../../components/coaching/CaddieProGate';

const CourseRecommender = () => {
  const { currentUser, showAlert } = useContext(AppContext);
  const isPremium = isUserPremium(currentUser);
  const [locationName, setLocationName] = useState(() => {
    const loc = currentUser.location;
    if (!loc?.address) return '';
    const parts = loc.address.split(',').map(p => p.trim());
    if (parts.length >= 3) return parts.slice(1, -1).join(', ');
    return loc.address;
  });
  const [coords, setCoords] = useState({
    lat: currentUser.location?.lat || null,
    lng: currentUser.location?.lon || null,
  });
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const geocodeLocation = async (address) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(address)}&format=json&limit=1&countrycodes=us`,
        { headers: { 'Accept-Language': 'en', 'User-Agent': 'LoftGolfApp/1.0' } }
      );
      const data = await response.json();
      if (data.length > 0) {
        return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
      }
    } catch (err) {
      console.error('Geocoding error:', err);
    }
    return null;
  };

  const findCourses = async () => {
    if (!locationName.trim()) {
      await showAlert('Please enter a location to search for courses.', 'Error');
      return;
    }
    setLoading(true);
    setError(null);
    setRecommendations(null);
    try {
      let searchCoords = coords;
      if (!searchCoords.lat || !searchCoords.lng) {
        searchCoords = await geocodeLocation(locationName.trim());
        if (!searchCoords) {
          setError('Could not find that location. Try a city name or zip code.');
          setLoading(false);
          return;
        }
      }

      if (isPremium) {
        const result = await firestoreService.getRecommendedCourses({
          handicap: currentUser.handicap || 0,
          skillLevel: currentUser.skillLevel || 'Beginner',
          lat: searchCoords.lat,
          lng: searchCoords.lng,
          locationName: locationName.trim(),
        });
        setRecommendations({ type: 'ai', items: result.recommendations });
      } else {
        const result = await firestoreService.getNearbyCourses({
          lat: searchCoords.lat,
          lng: searchCoords.lng,
          locationName: locationName.trim(),
        });
        setRecommendations({ type: 'distance', items: result.courses });
      }
    } catch (err) {
      console.error('Course recommendation error:', err);
      setError('Unable to find courses right now. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-4">
      <h2 className="text-2xl font-bold text-white mb-4 flex items-center space-x-2">
        <Flag className="w-6 h-6 text-[#16a34a]" />
        <span>Find a Course</span>
      </h2>

      {!isPremium && (
        <div className="mb-4 p-3 bg-yellow-600/10 border border-yellow-600/30 rounded-lg">
          <p className="text-sm text-gray-300">
            <button onClick={() => setShowUpgradeModal(true)} className="text-yellow-400 font-semibold hover:text-yellow-300 underline">
              Upgrade to Caddie Pro
            </button>
            {' '}to get Personalized AI course recommendations.
          </p>
        </div>
      )}

      <div className="mb-6">
        <label className="block text-sm font-medium text-white mb-2">Search location</label>
        <div className="flex space-x-2">
          <input
            type="text"
            value={locationName}
            onChange={(e) => { setLocationName(e.target.value); setCoords({ lat: null, lng: null }); }}
            onKeyPress={(e) => e.key === 'Enter' && findCourses()}
            placeholder="City, state, or zip code..."
            className="flex-1 px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black min-w-0"
          />
          <button
            onClick={findCourses}
            disabled={loading}
            className="px-4 py-2 bg-[#16a34a] text-white rounded-lg font-medium hover:bg-[#15803d] disabled:opacity-50 flex-shrink-0"
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </div>

      {loading && (
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="bg-gray-800 rounded-lg h-20" />)}
        </div>
      )}

      {error && (
        <div className="rounded-lg p-4" style={{ backgroundColor: 'rgb(var(--color-warning-red-bg) / 0.3)', borderWidth: '1px', borderColor: 'rgb(var(--color-warning-red-border))' }}>
          <p className="text-sm" style={{ color: 'rgb(var(--color-warning-red))' }}>{error}</p>
        </div>
      )}

      {recommendations?.type === 'ai' && (
        <div className="space-y-4">
          {recommendations.items.map((course, i) => (
            <div key={i} className="bg-gray-900 border border-gray-700 rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-white font-bold text-lg">{course.courseName}</h3>
                <span className="text-sm font-bold text-[#16a34a] flex-shrink-0 ml-2">{course.matchScore}%</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2 mb-3">
                <div
                  className="bg-[#16a34a] h-2 rounded-full transition-all duration-500"
                  style={{ width: `${course.matchScore}%` }}
                />
              </div>
              <p className="text-gray-300 text-sm mb-3">{course.reason}</p>
              {course.distance > 0 && (
                <p className="text-gray-500 text-xs mb-2">{course.distance} miles away</p>
              )}
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(course.courseName)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-1 text-sm text-[#16a34a] hover:text-[#15803d] font-medium"
              >
                <Navigation className="w-4 h-4" />
                <span>Get Directions</span>
              </a>
            </div>
          ))}
        </div>
      )}

      {recommendations?.type === 'distance' && (
        <div className="space-y-3">
          {recommendations.items.map((course, i) => (
            <div key={i} className="bg-gray-900 border border-gray-700 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-white font-bold">{course.courseName}</h3>
                  {course.city && course.state && (
                    <p className="text-gray-400 text-sm">{course.city}, {course.state}</p>
                  )}
                </div>
                {course.distance > 0 && (
                  <span className="text-gray-400 text-sm flex-shrink-0 ml-2">{course.distance} mi</span>
                )}
              </div>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(course.courseName)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-1 text-sm text-[#16a34a] hover:text-[#15803d] font-medium mt-2"
              >
                <Navigation className="w-4 h-4" />
                <span>Get Directions</span>
              </a>
            </div>
          ))}
        </div>
      )}

      {!loading && !recommendations && !error && (
        <div className="text-center py-12">
          <Flag className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-400">Search for a location to find golf courses near you.</p>
        </div>
      )}

      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowUpgradeModal(false)}>
          <div className="relative w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowUpgradeModal(false)} className="absolute top-6 right-6 text-gray-400 hover:text-white z-10">
              <X className="w-5 h-5" />
            </button>
            <CaddieProGate />
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseRecommender;
